import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from '@ff-os/shared-react/hooks/useDebounce';
import { usePrevious } from '@ff-os/shared-react/hooks/usePrevious';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';

/**
 * Ensures correct and latest message is always displayed.
 */
export const usePhotoPrintGroupPanelMessage = (
    activeVariant: PhotoPrintsJson.Variant,
    isNewGroup: boolean,
    successfulUploadsCount: number,
    failedUploadsCount: number
): Message | null => {

    const previousVariant = usePrevious(activeVariant);
    const previousSuccessfulUploadsCount = usePrevious(successfulUploadsCount);
    const previousFailedUploadsCount = usePrevious(failedUploadsCount);

    const [message, setMessage] = useState<Message | null>(null);

    const removeMessage = useCallback((messageToRemove: Message) => {
        setMessage(message => messageToRemove === message
            ? null
            : message);
    }, []);

    const debouncedRemoveMessage = useDebounce(removeMessage, 2000);

    const toggleMessage = useCallback((message: Message, show: boolean) => {
        if (show) {
            setMessage(message);
        } else {
            removeMessage(message);
        }
    }, [removeMessage]);

    useEffect(() => {
        if (successfulUploadsCount !== previousSuccessfulUploadsCount) {
            toggleMessage('successful-uploads', successfulUploadsCount > 0);
        }
    }, [previousSuccessfulUploadsCount, successfulUploadsCount, toggleMessage]);

    useEffect(() => {
        if (failedUploadsCount !== previousFailedUploadsCount) {
            toggleMessage('failed-uploads', failedUploadsCount > 0);
        }
    }, [failedUploadsCount, previousFailedUploadsCount, toggleMessage]);

    useEffect(() => {
        if (activeVariant.id !== previousVariant.id) {
            setMessage('changed-variant');
            debouncedRemoveMessage('changed-variant');
        }
    }, [activeVariant.id, previousVariant.id, debouncedRemoveMessage]);

    useEffect(() => {
        if (isNewGroup) {
            setMessage('group-added');
            debouncedRemoveMessage('group-added');
        }
    }, [isNewGroup, debouncedRemoveMessage]);

    return message;
};

type SuccessMessage = 'successful-uploads' | 'group-added' | 'changed-variant';
type ErrorMessage = 'failed-uploads';
type Message = SuccessMessage | ErrorMessage;

const successMessages: SuccessMessage[] = ['successful-uploads', 'group-added', 'changed-variant'];
export const isSuccessMessage = (message: Message | null): message is SuccessMessage => {
    return message !== null
        && successMessages.includes(message as SuccessMessage);
};

const errorMessages: ErrorMessage[] = ['failed-uploads'];
export const isErrorMessage = (message: Message | null): message is ErrorMessage => {
    return message !== null
        && errorMessages.includes(message as ErrorMessage);
};